import { useRef, useState } from "react";
import { FileText, Loader2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fileToJpegBase64, mapProduct } from "@/lib/veeder";
import { useT } from "@/lib/i18n";
import { formatGal, productColor, productName, PRODUCTS } from "@/lib/tank";
import { tankPatch } from "@/components/drop/SiteSwitcher";

// One row per grade: compartments with the same product are added together.
function totals(lines) {
  const by = new Map();
  for (const ln of lines) {
    const grade = mapProduct(ln.product);
    const key = grade || ln.product || "?";
    const cur = by.get(key) || { product: ln.product, grade, gross: 0, net: 0, count: 0, missGross: false, missNet: false, parts: [] };
    cur.gross += ln.gross || 0; cur.net += ln.net || 0; cur.count += 1;
    cur.parts.push({ ...ln, grade });
    cur.missGross ||= !ln.gross; cur.missNet ||= !ln.net;
    by.set(key, cur);
  }
  return [...by.values()].map((x) => ({ ...x, gross: x.missGross ? null : x.gross, net: x.missNet ? null : x.net }));
}


function Pick({ ln, id, used, onUse, t }) {
  return (
    <span className="bol-pick">
      {["net", "gross"].map((k) => ln[k] ? (
        <button key={k} type="button" className={`chip ${used === `${id}-${k}` ? "chip-active" : ""}`} data-testid={`bol-use-${id}-${k}`} onClick={() => onUse(ln, id, k)}>
          {k === "net" ? t("Net") : t("Gross")} {formatGal(ln[k])}
        </button>
      ) : null)}
    </span>
  );
}

export function BolScan({ s, patch }) {
  const t = useT();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [bol, setBol] = useState(null);
  const [err, setErr] = useState("");
  const [used, setUsed] = useState(null);

  const scan = async (file) => {
    if (!file) return;
    setBusy(true); setErr(""); setUsed(null);
    try { setBol(await api.scanBol(await fileToJpegBase64(file, 2000))); }
    catch (e) { setErr(apiError(e) || t("Couldn't read that photo. Try a sharper, straight-on photo.")); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };

  // At a saved station, jump to the tank holding that grade.
  const use = async (ln, id, kind) => {
    const product = ln.grade;
    let tank = {};
    if (product && s.activeSiteId) {
      const site = (await api.getSites().catch(() => [])).find((x) => x.id === s.activeSiteId);
      const idx = site ? site.tanks.findIndex((x) => x.product === product) : -1;
      if (idx >= 0) tank = tankPatch(site, idx);
    }
    patch({ ...(product ? { product } : {}), ...tank, dropGallons: Math.round(ln[kind]) });
    setUsed(`${id}-${kind}`);
  };

  return (
    <div className="bol" data-testid="bol-scan">
      <button type="button" className="load-btn bol-btn" data-testid="bol-scan-button" disabled={busy} onClick={() => input.current?.click()}>
        {busy ? <Loader2 size={14} className="spin" /> : <FileText size={14} />}{busy ? t("Reading BOL…") : t("Scan BOL")}
      </button>
      <input ref={input} type="file" accept="image/*" hidden data-testid="bol-scan-input" onChange={(e) => scan(e.target.files?.[0])} />
      {err && <div className="form-error" data-testid="bol-error">{err}</div>}
      {bol && (
        <div className="bol-card" data-testid="bol-result">
          <div className="bol-head">
            <span className="label">{t("BOL")} {bol.bol_number ? `#${bol.bol_number}` : ""}{bol.terminal ? ` · ${bol.terminal}` : ""}</span>
            <button type="button" className="icon-btn icon-btn-sm" data-testid="bol-close" aria-label={t("Close")} onClick={() => setBol(null)}><X size={14} /></button>
          </div>
          {totals(bol.lines).map((ln, i) => {
            const p = ln.grade;
            return (
              <div key={i} className="bol-line" data-testid={`bol-line-${i}`}>
                <span className="bol-prod"><span className="grade-dot" style={{ background: productColor(p) }} />{p ? t(productName(p)) : ln.product || t("Unknown")}
                  {ln.count > 1 && <span className="card-sub bol-count" data-testid={`bol-count-${i}`}>{t("{n} compartments total", { n: ln.count })}</span>}</span>
                <Pick ln={ln} id={`${i}`} used={used} onUse={use} t={t} />
                {ln.count > 1 && (
                  <details className="bol-parts" data-testid={`bol-parts-${i}`}>
                    <summary data-testid={`bol-parts-toggle-${i}`}>{t("Split load? Pick one compartment")}</summary>
                    {ln.parts.map((pt, j) => (
                      <div key={j} className="bol-part" data-testid={`bol-part-${i}-${j}`}>
                        <span className="card-sub">{t("Compartment {c}", { c: pt.compartment || j + 1 })}</span>
                        <Pick ln={pt} id={`${i}-${j}`} used={used} onUse={use} t={t} />
                      </div>
                    ))}
                  </details>
                )}
              </div>
            );
          })}
          <div className="bol-total mono" data-testid="bol-grand-total">{t("Load total")}: {t("Net")} {formatGal(bol.lines.reduce((a, l) => a + (l.net || 0), 0))} · {t("Gross")} {formatGal(bol.lines.reduce((a, l) => a + (l.gross || 0), 0))}</div>
          <div className="field-hint">{t("Tap Net or Gross to fill in the drop. Always check the numbers against the paper BOL.")}</div>
        </div>
      )}
    </div>
  );
}
